/**
 * What a reviewer is being asked to clear — decided here, in a module with no network in it.
 *
 * The rule is small but it is a policy, not a formatting detail, so it is tested. It lives apart
 * from the API client because that client pulls in the auth stack, and a rule this important
 * should be testable without standing up a browser.
 */

import type { Barnskill } from './barnskillsApi';

/**
 * Skills waiting on a reviewer.
 *
 * `changes_requested` is excluded on purpose: the ball is with the author, and a number the
 * reviewer cannot reduce is one they learn to ignore. An absent status means a record written
 * before the field existed — every one of those is a published catalog seed.
 */
export function skillsAwaitingReview<T extends Pick<Barnskill, 'status' | 'retired'>>(skills: T[]): T[] {
  return skills.filter((s) => !s.retired && s.status === 'in_review');
}

export function countSkillsAwaitingReview(skills: Pick<Barnskill, 'status' | 'retired'>[]): number {
  return skillsAwaitingReview(skills).length;
}

/**
 * Where "review the skill" should actually go.
 *
 * A count is not a destination. Sending a reviewer to a filtered list when exactly one thing is
 * waiting costs them a second click to reach the only possible answer, and — as reported from RnD
 * on 2026-07-31 — reads as a dead end, because the alert names a number and offers nothing that
 * opens the thing the number refers to. One pending skill deep-links to that skill; more than one
 * needs the list, because there is a genuine choice to make.
 */
export function reviewSkillsHref(pending: Pick<Barnskill, 'name'>[]): string {
  return pending.length === 1
    ? `/skills/${encodeURIComponent(pending[0].name)}`
    : '/skills?filter=attention';
}

/** "3 skills and 1 article" — plural-correct, and silent about whichever kind is empty. */
export function describePending(skills: number, articles: number): string {
  const parts: string[] = [];
  if (skills > 0) parts.push(`${skills} skill${skills === 1 ? '' : 's'}`);
  if (articles > 0) parts.push(`${articles} article${articles === 1 ? '' : 's'}`);
  return parts.join(' and ');
}
